'use client';

import { Box, Skeleton, Stack, Grid } from '@mui/material';
import { memo } from 'react';

export const CardShimmer = memo(() => (
  <Box sx={{ p: 2, mb: 2 }}>
    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 1 }} />
    <Skeleton variant="text" width="60%" />
    <Skeleton variant="text" width="40%" />
  </Box>
));

CardShimmer.displayName = 'CardShimmer';

export const ListShimmer = memo(({ count = 3 }) => (
  <Stack spacing={2}>
    {[...Array(count)].map((_, i) => (
      <CardShimmer key={i} />
    ))}
  </Stack>
));

ListShimmer.displayName = 'ListShimmer';

export const BookingHistoryShimmer = memo(() => (
  <Box sx={{ maxWidth: 520, mx: 'auto', px: 2 }}>
    <Skeleton variant="text" height={40} width="60%" sx={{ mx: 'auto', mb: 3 }} />
    <Stack direction="row" spacing={2} mb={3}>
      <Skeleton variant="rounded" width={80} height={32} />
      <Skeleton variant="rounded" width={80} height={32} />
      <Skeleton variant="rounded" width={80} height={32} />
    </Stack>
    <ListShimmer count={5} />
  </Box>
));

BookingHistoryShimmer.displayName = 'BookingHistoryShimmer';

export const ProfileShimmer = memo(() => (
  <Box sx={{ p: 3 }}>
    <Skeleton variant="circular" width={80} height={80} sx={{ mx: 'auto', mb: 2 }} />
    <Skeleton variant="text" height={30} width="50%" sx={{ mx: 'auto', mb: 3 }} />
    {[...Array(4)].map((_, i) => (
      <Box key={i} sx={{ mb: 2 }}>
        <Skeleton variant="text" width="30%" sx={{ mb: 0.5 }} />
        <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
      </Box>
    ))}
  </Box>
));

ProfileShimmer.displayName = 'ProfileShimmer';

export const ParcelsGridShimmer = memo(({ count = 9 }) => (
  <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 } }}>
    {/* Header: page title and search */}
    <Box sx={{ pt: 4, pb: 2 }}>
      <Skeleton animation="wave" variant="text" width={220} height={36} sx={{ mb: 2 }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Skeleton animation="wave" variant="rounded" width={{ xs: '100%', sm: 280 }} height={44} />
        <Skeleton animation="wave" variant="rounded" width={80} height={44} />
        <Skeleton animation="wave" variant="rounded" width={80} height={44} />
      </Stack>
    </Box>

    {/* Stats row shimmer */}
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ py: 2 }}>
      <Skeleton animation="wave" variant="rounded" width={160} height={64} />
      <Skeleton animation="wave" variant="rounded" width={160} height={64} />
      <Skeleton animation="wave" variant="rounded" width={160} height={64} />
    </Stack>

    {/* Search section */}
    <Box sx={{ py: 2 }}>
      <Skeleton animation="wave" variant="rounded" width="100%" height={52} />
    </Box>

    {/* Grid of parcels */}
    <Box>
      <Grid container spacing={3}>
        {[...Array(count)].map((_, idx) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 3 }}>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Skeleton animation="wave" variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton animation="wave" variant="text" width="60%" height={18} />
                    <Skeleton animation="wave" variant="text" width="40%" height={14} />
                  </Box>
                </Stack>
                <Skeleton
                  animation="wave"
                  variant="rectangular"
                  height={96}
                  sx={{ borderRadius: 2 }}
                />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Skeleton animation="wave" variant="rounded" width={90} height={28} />
                  <Skeleton animation="wave" variant="rounded" width={60} height={28} />
                </Stack>
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  </Box>
));

ParcelsGridShimmer.displayName = 'ParcelsGridShimmer';

// Default wrapper component that accepts 'variant' prop for different shimmer variants
export default function LoadingShimmer({ variant = 'default', count = 3 }) {
  switch (variant) {
    case 'bookingHistory':
      return <BookingHistoryShimmer />;
    case 'profile':
      return <ProfileShimmer />;
    case 'parcelsGrid':
      return <ParcelsGridShimmer count={count} />;
    case 'list':
      return <ListShimmer count={count} />;
    case 'card':
      return <CardShimmer />;
    case 'summary':
    case 'payment':
      // For summary/payment variants, show a card shimmer plus a centered skeleton
      return (
        <Box sx={{ px: 2 }}>
          <CardShimmer />
          <CardShimmer />
        </Box>
      );
    default:
      return <ListShimmer count={count} />;
  }
}
